import { useTranslations } from "@zapier/i18n";
import {
  Checkbox,
  Field,
  Select,
  SelectItem,
  TextInput,
} from "@zapier/design-system";
import { Controller, useFormContext } from "react-hook-form";
import { EmbedDomainRestrictionField } from "./EmbedDomainRestrictionField";
import { buildEmbedSnippet, SnippetType } from "./embedSnippet";
import { EmbedSettingsForm } from "./InterfaceShareModal";
import { PageToEmbedField } from "./PageToEmbedField";

import styles from "./EmbedSettings.module.css";

const SNIPPET_TYPE_SCRIPT = "script" as const;
const SNIPPET_TYPE_IFRAME = "iframe" as const;

export interface EmbedSettingsProps {
  interfaceAsset: {
    id: string;
    loginPageId?: string;
  };
}

export function EmbedSettings({ interfaceAsset }: EmbedSettingsProps) {
  const t = useTranslations("embed-settings");

  const { control, register, watch } = useFormContext<EmbedSettingsForm>();

  const values = watch();

  return (
    <div className={styles.root}>
      <PageToEmbedField
        interfaceId={interfaceAsset.id}
        loginPageId={interfaceAsset.loginPageId}
      />
      <EmbedDomainRestrictionField interfaceId={interfaceAsset.id} />
      <Field
        label={t("labels.frame-height")}
        renderInput={(inputProps) => (
          <TextInput {...inputProps} {...register("frameHeight")} type="text" />
        )}
      />
      <Field
        label={t("labels.frame-width")}
        renderInput={(inputProps) => (
          <TextInput {...inputProps} {...register("frameWidth")} type="text" />
        )}
      />
      <Field
        label={t("labels.code-snippet-type")}
        renderInput={() => (
          <Controller
            control={control}
            name="snippetType"
            render={({ field }) => (
              <Select
                label={t("labels.code-snippet-type")}
                isLabelHidden
                selectedKey={field.value ?? undefined}
                onChange={(key) => field.onChange(key as SnippetType)}
              >
                <SelectItem value={SNIPPET_TYPE_SCRIPT}>
                  {SNIPPET_TYPE_SCRIPT}
                </SelectItem>
                <SelectItem value={SNIPPET_TYPE_IFRAME}>
                  {SNIPPET_TYPE_IFRAME}
                </SelectItem>
              </Select>
            )}
          />
        )}
        helpTextLineClamp={2}
        renderHelpText={() => (
          <>{t("help-text.code-snippet-type-description")}</>
        )}
      />
      <Controller
        control={control}
        name="includeBackground"
        render={({ field }) => (
          <Checkbox
            isSelected={field.value}
            value={field.value.toString()}
            onChange={() => field.onChange(!field.value)}
          >
            {t("checkboxes.include-background")}
          </Checkbox>
        )}
      />
      <Controller
        control={control}
        name="allowQueryParameters"
        render={({ field }) => (
          <Checkbox
            isSelected={field.value}
            value={field.value.toString()}
            onChange={() => field.onChange(!field.value)}
          >
            {t("checkboxes.allow-query-params")}
          </Checkbox>
        )}
      />
      <pre className={styles.snippet}>{buildEmbedSnippet(values)}</pre>
    </div>
  );
}
